import React from 'react';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog.js';
import { withTranslation } from 'react-i18next';
import { Check, CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBox, Search, Close } from '@mui/icons-material';
import GroupCard from 'components/GroupCard/GroupCard.js';
import { FlexContainer } from 'components/FlexContainer/FlexContainer.jsx';
import styles from './Step2.module.scss';

// Offers using 'membership_card' (or its once-only variant 'limited_membership_card', see
// Step1.js's LIMITED_ACTION_TYPE_MAP) show the member's card of the offer's target group when
// activated (spara-app's OfferActivateModal getMembershipGroup()) — so every selected group
// must actually have a membership card enabled on it, or activating members of a
// no-membership-card group would have nothing to show. This applies just as much to a chosen
// special distribution (Almennt/Sparakjör/B2B) as to specific markhópar: "Almennt" (id 0) has
// no backing Torgid_special_company row at all (so it never has a card), "Sparakjör" (id 1) and
// "B2B" (id 3) are real rows there and are checked by their own has_membership_card field.
const MEMBERSHIP_CARD_ACTION_TYPES = ['membership_card', 'limited_membership_card'];

// Special distribution ids and their translation keys, shared between the selector UI
// (render()) and the membership-card validation (isValidated()).
const SPECIAL_DISTRIBUTION_LABEL_KEYS = {
    0: 'offer.special_companies_Default',
    1: 'offer.special_companies_Lykilkjör',
    3: 'offer.special_companies_B2B',
};

class Step2 extends React.Component {
    constructor(props) {
        super(props);
        this.store = JSON.parse(localStorage.getItem('user'));
        this.user_is_group_admin = !!(this.store && this.store.roles.includes('group_admin'));
        this.isAdmin = !!(
            this.store &&
            this.store.permission &&
            this.store.permission.includes('offer-create')
        );

        const offerData = props.offerData || {};
        const hasPrefill = !!(offerData.id || offerData.isDuplicate);
        const specialIds = [0, 1, 3];
        let initialIds;
        if (!hasPrefill) {
            // New offers require an explicit distribution/group selection. Do not
            // silently select the only accessible group, since that selection can be
            // missed visually and later produce misleading validation errors.
            initialIds = [];
        } else {
            if (offerData.group_ids) {
                initialIds = offerData.group_ids.split(',').map(Number);
            } else if (offerData.torgid_special_company_id) {
                initialIds = [offerData.torgid_special_company_id];
            } else {
                initialIds = [0];
            }
        }

        // Legacy/edited offers can have group_ids containing a special distribution id
        // (0/1/3 — Almennt/Sparakjör/B2B) alongside leftover real group ids (e.g. saved before
        // a distribution change, or edited outside this wizard). The special selection wins:
        // real ids are kept in savedGroupIds so switching to "Markhópar" mode later restores
        // them, but they must not render or count as selected markhópar while a special option
        // is active.
        const specialSelection = initialIds.find((id) => specialIds.includes(id));
        const hasRealGroups = initialIds.some((id) => !specialIds.includes(id));
        const realGroupIds = initialIds.filter((id) => !specialIds.includes(id));

        this.state = {
            torgid_special_company_ids:
                specialSelection !== undefined ? [specialSelection] : initialIds,
            savedGroupIds: realGroupIds,
            groupSelectorVisible: hasRealGroups && specialSelection === undefined,
            searchQuery: '',
            dialogOpen: false,
            dialogMessage: null,
        };
    }

    componentDidMount() {
        // The available groups (this.props.data) are already loaded by the time Step2
        // mounts (addOffer.js gates step content on isDataLoading), so the data-change
        // branch in componentDidUpdate below never fires for the initial selection.
        // Run the stale-id cleanup once on mount so deleted/hidden groups baked into a
        // saved offer's group_ids don't linger silently in the selection.
        this.cleanupStaleGroupIds();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.userHasCompanyAccess && !this.props.userHasCompanyAccess) {
            const specialIds = [0, 1, 3];
            const currentGroups = this.state.torgid_special_company_ids.filter(
                (id) => !specialIds.includes(id)
            );
            this.notifyStateChange({
                torgid_special_company_ids: currentGroups,
                groupSelectorVisible: true,
                savedGroupIds: currentGroups.length > 0 ? currentGroups : this.state.savedGroupIds,
            });
            return;
        }

        // Also re-run cleanup if the available groups ever change after mount.
        if (prevProps.data !== this.props.data) {
            this.cleanupStaleGroupIds();
        }
    }

    // Remove selected ids that no longer correspond to a group the admin can see and
    // deselect here. Offers and cached user data can still reference groups that were
    // deleted or hidden; the API never returns those (see Torgid_special_company_controller
    // index: where('deleted', false)), so they render no card and are impossible to toggle
    // off. Left in state they silently fail membership-card validation and get re-saved onto
    // the offer, so we strip them from both the active selection and the saved-groups backup.
    cleanupStaleGroupIds = () => {
        if (!this.props.data?.results) return;

        const specialIds = [0, 1, 3];
        const visibleGroupIds = new Set(this.getVisibleGroups().map((group) => group.id));

        const cleanedIds = this.state.torgid_special_company_ids.filter(
            (id) => specialIds.includes(id) || visibleGroupIds.has(id)
        );
        const cleanedSaved = this.state.savedGroupIds.filter((id) => visibleGroupIds.has(id));

        const idsChanged = cleanedIds.length !== this.state.torgid_special_company_ids.length;
        const savedChanged = cleanedSaved.length !== this.state.savedGroupIds.length;
        if (!idsChanged && !savedChanged) return;

        const update = {
            torgid_special_company_ids: cleanedIds,
            savedGroupIds: cleanedSaved,
        };
        // Only touch selector visibility when the *active* selection changed; if a special
        // distribution is selected we must not force the group selector open just because we
        // trimmed the saved-groups backup.
        if (idsChanged) {
            const cleanedActiveGroupIds = cleanedIds.filter((id) => !specialIds.includes(id));
            update.groupSelectorVisible =
                this.state.groupSelectorVisible || cleanedActiveGroupIds.length > 0;
        }

        this.notifyStateChange(update);
    };

    sendState() {
        return this.state;
    }

    hideAlert = () => {
        this.setState({ dialogOpen: false, dialogMessage: null });
    };

    isValidated() {
        const { t, actionType, data } = this.props;
        const { torgid_special_company_ids, groupSelectorVisible } = this.state;
        const specialIds = [0, 1, 3];
        const selectedSpecial = torgid_special_company_ids.find((id) => specialIds.includes(id));
        const selectedGroupIds = torgid_special_company_ids.filter(
            (id) => !specialIds.includes(id)
        );
        const hasSelection =
            selectedSpecial !== undefined ||
            (groupSelectorVisible && selectedGroupIds.length > 0);

        let errorMessage = null;
        if (!hasSelection) {
            errorMessage = 'Vinsamlega veldu dreifingu eða einn eða fleiri markhópa.';
        } else if (groupSelectorVisible && selectedGroupIds.length === 0) {
            errorMessage = 'Vinsamlega veldu einn eða fleiri markhópa.';
        } else if (MEMBERSHIP_CARD_ACTION_TYPES.includes(actionType)) {
            const groups = data?.results || [];
            if (selectedSpecial !== undefined) {
                // "Almennt" (id 0) has no backing Torgid_special_company row (so it never
                // qualifies); "Sparakjör"/"B2B" (1/3) are real rows checked by their own field.
                const specialGroup = groups.find((g) => g.id === selectedSpecial);
                if (!specialGroup?.has_membership_card) {
                    const label = t(
                        SPECIAL_DISTRIBUTION_LABEL_KEYS[selectedSpecial] ?? 'special_companies'
                    );
                    errorMessage = t('offer.validation_membership_card_group', { groups: label });
                }
            } else if (groupSelectorVisible && selectedGroupIds.length > 0) {
                // Ignore selected ids the admin can't see/deselect here (e.g. stale ids no
                // longer in the visible list) when deciding *which* groups lack a card — but if
                // that leaves nothing to check, there is by definition no group with a card
                // backing this offer, so it must still block rather than silently pass.
                const visibleGroupIds = this.getVisibleGroups().map((g) => g.id);
                const visibleSelectedGroupIds = selectedGroupIds.filter((id) =>
                    visibleGroupIds.includes(id)
                );
                if (visibleSelectedGroupIds.length === 0) {
                    errorMessage = t('offer.validation_membership_card_no_valid_groups');
                } else {
                    const groupsWithoutCard = visibleSelectedGroupIds
                        .map((id) => groups.find((g) => g.id === id))
                        .filter((g) => g && !g.has_membership_card);
                    if (groupsWithoutCard.length > 0) {
                        const names = groupsWithoutCard.map((g) => g.name).filter(Boolean).join(', ');
                        errorMessage = t('offer.validation_membership_card_group', { groups: names });
                    }
                }
            }
        }

        if (errorMessage) {
            this.setState({ dialogOpen: true, dialogMessage: errorMessage });
            return false;
        }
        return true;
    }

    notifyStateChange = (stateUpdate) => {
        this.setState(stateUpdate, () => {
            if (this.props.onStateChange) {
                this.props.onStateChange(this.state);
            }
        });
    };

    handleToggleAll = (groupIds) => {
        const { torgid_special_company_ids } = this.state;
        const specialIds = [0, 1, 3];
        const allSelected = groupIds.every((id) => torgid_special_company_ids.includes(id));
        const currentWithoutSpecial = torgid_special_company_ids.filter(
            (id) => !specialIds.includes(id)
        );
        if (allSelected) {
            const newIds = currentWithoutSpecial.filter((id) => !groupIds.includes(id));
            this.notifyStateChange({
                torgid_special_company_ids: newIds,
                savedGroupIds: newIds,
                groupSelectorVisible: true,
            });
        } else {
            const newIds = [...new Set([...currentWithoutSpecial, ...groupIds])];
            this.notifyStateChange({
                torgid_special_company_ids: newIds,
                savedGroupIds: newIds,
                groupSelectorVisible: true,
            });
        }
    };

    handleToggle = (id) => {
        const { torgid_special_company_ids } = this.state;
        const specialIds = [0, 1, 3];

        if (id === 'groups') {
            const restored = this.state.savedGroupIds;
            this.notifyStateChange({
                groupSelectorVisible: true,
                torgid_special_company_ids: restored.length > 0 ? restored : [],
            });
        } else if (specialIds.includes(id)) {
            const currentGroups = torgid_special_company_ids.filter(
                (x) => !specialIds.includes(x)
            );
            this.notifyStateChange({
                torgid_special_company_ids: [id],
                groupSelectorVisible: false,
                savedGroupIds: currentGroups.length > 0 ? currentGroups : this.state.savedGroupIds,
            });
        } else {
            const withoutSpecial = torgid_special_company_ids.filter(
                (x) => !specialIds.includes(x)
            );
            const idx = withoutSpecial.indexOf(id);
            if (idx > -1) {
                withoutSpecial.splice(idx, 1);
            } else {
                withoutSpecial.push(id);
            }
            const newIds = [...withoutSpecial];
            this.notifyStateChange({
                torgid_special_company_ids: newIds,
                savedGroupIds: newIds,
                groupSelectorVisible: true,
            });
        }
    };

    getVisibleGroups = () => {
        const { data, userHasCompanyAccess } = this.props;
        if (!data || !data.results) return [];

        const myGroupIds = (this.store.groupIds || []).filter(Boolean);
        const isCompanyUser = this.store.roles?.includes('company_user');

        const allGroups = data.results.filter((item) => item.id !== 1 && item.id !== 3);

        // When user does not have access to the selected company,
        // only show their own groups (they must be a group_admin to proceed)
        if (!this.isAdmin && !userHasCompanyAccess) {
            return allGroups
                .filter((item) => myGroupIds.includes(item.id))
                .map((item) => ({ ...item, _isMyGroup: true }));
        }

        // Pure group admins (not also a company user or super admin) only see their own groups
        const isPureGroupAdmin = this.user_is_group_admin && !this.isAdmin && !isCompanyUser;
        if (isPureGroupAdmin) {
            return allGroups
                .filter((item) => myGroupIds.includes(item.id))
                .map((item) => ({ ...item, _isMyGroup: true }));
        }

        // Everyone else: their groups first, then all visible public groups
        const myGroups = allGroups
            .filter((item) => myGroupIds.includes(item.id))
            .map((item) => ({ ...item, _isMyGroup: true }));

        const otherGroups = allGroups.filter((item) => {
            if (myGroupIds.includes(item.id)) return false;
            if (this.isAdmin) return true;
            return !item.hidden;
        });

        return [...myGroups, ...otherGroups];
    };

    groupByCategory = (groups) => {
        const { t } = this.props;
        const result = {};
        const categoryOrder = {};

        groups.forEach((item) => {
            const categoryName = item.group?.name;
            const displayKey = categoryName || t('other');
            if (!result[displayKey]) {
                result[displayKey] = [];
                categoryOrder[displayKey] = item.group?.order ?? Infinity;
            }
            result[displayKey].push(item);
        });

        // Sort: by group.order, uncategorized last
        const other = t('other');
        const sorted = {};
        const keys = Object.keys(result).sort((a, b) => {
            if (a === other) return 1;
            if (b === other) return -1;
            return (categoryOrder[a] ?? Infinity) - (categoryOrder[b] ?? Infinity);
        });
        keys.forEach((k) => (sorted[k] = result[k]));
        return sorted;
    };

    renderGroupGrid = (groupList, label, isMine = false) => {
        const { torgid_special_company_ids } = this.state;
        const groupIds = groupList.map((g) => g.id);
        const selectedCount = groupIds.filter((id) => torgid_special_company_ids.includes(id))
            .length;
        const allSelected = selectedCount === groupIds.length && groupIds.length > 0;
        const someSelected = selectedCount > 0 && !allSelected;

        return (
            <>
                <div className={styles.categoryHeader}>
                    <button
                        type="button"
                        className={styles.selectAllBtn}
                        onClick={() => this.handleToggleAll(groupIds)}
                        title={allSelected ? 'Afvelja alla' : 'Velja alla'}
                    >
                        {allSelected ? (
                            <CheckBox
                                className={styles.checkboxIcon}
                                style={{ color: '#4caf50' }}
                            />
                        ) : someSelected ? (
                            <IndeterminateCheckBox
                                className={styles.checkboxIcon}
                                style={{ color: '#4caf50' }}
                            />
                        ) : (
                            <CheckBoxOutlineBlank className={styles.checkboxIcon} />
                        )}
                        <span className={styles.subSectionLabel} style={{ margin: 0 }}>
                            {label}
                        </span>
                        {selectedCount > 0 && (
                            <span className={styles.selectedCount}>{selectedCount}</span>
                        )}
                    </button>
                </div>
                <div className={styles.groupsGrid}>
                    {groupList.map((group) => {
                        const isSelected = torgid_special_company_ids.includes(group.id);
                        return (
                            <div
                                key={group.id}
                                className={`${styles.groupCardWrapper} ${
                                    isMine ? styles.groupCardWrapperMine : ''
                                } ${isSelected ? styles.groupCardWrapperSelected : ''}`}
                            >
                                <GroupCard
                                    group={group}
                                    onClick={(g) => this.handleToggle(g.id)}
                                    hideBadges
                                    isSelected={isSelected}
                                />
                            </div>
                        );
                    })}
                </div>
            </>
        );
    };

    render() {
        const { t, userHasCompanyAccess } = this.props;
        const { torgid_special_company_ids, groupSelectorVisible } = this.state;
        const groups = this.getVisibleGroups();
        const isCompanyUser = this.store.roles?.includes('company_user');
        const isPureGroupAdmin = this.user_is_group_admin && !this.isAdmin && !isCompanyUser;
        const noCompanyAccess = !this.isAdmin && !userHasCompanyAccess;
        const showSpecialOptions = !isPureGroupAdmin && !noCompanyAccess;

        const specialIds = [0, 1, 3];
        const selectedSpecial = torgid_special_company_ids.find((id) => specialIds.includes(id));
        // Only count ids that actually render as a selectable card below — a selected id with
        // no matching visible group can't be reviewed/deselected here, so it shouldn't be
        // reflected in the "X hópar valdir" summary either.
        const visibleGroupIds = groups.map((g) => g.id);
        const selectedGroupCount = torgid_special_company_ids.filter(
            (id) => !specialIds.includes(id) && visibleGroupIds.includes(id)
        ).length;
        const hasSelection = selectedSpecial !== undefined || selectedGroupCount > 0;

        const { searchQuery } = this.state;
        const filteredGroups = searchQuery.trim()
            ? groups.filter((g) => {
                  const q = searchQuery.toLowerCase();
                  return (
                      g.name?.toLowerCase().includes(q) ||
                      g.group?.name?.toLowerCase().includes(q)
                  );
              })
            : groups;

        const specialOptions = [
            {
                id: 0,
                labelKey: SPECIAL_DISTRIBUTION_LABEL_KEYS[0],
                color: '#607d8b',
                description: t('offer.special_companies_default_desc'),
            },
            {
                id: 1,
                labelKey: SPECIAL_DISTRIBUTION_LABEL_KEYS[1],
                color: '#c0392b',
                description: t('offer.special_companies_lykilkjor_desc'),
            },
            {
                id: 3,
                labelKey: SPECIAL_DISTRIBUTION_LABEL_KEYS[3],
                color: '#2980b9',
                description: t('offer.special_companies_b2b_desc'),
            },
            {
                id: 'groups',
                labelKey: 'special_companies',
                color: '#2e7d32',
                description: t('target_groups_description'),
            },
        ];

        return (
            <div className={styles.container}>
                <ConfirmDialog
                    open={!!this.state.dialogOpen}
                    title={t('missing_information')}
                    message={this.state.dialogMessage}
                    type="warning"
                    onConfirm={() => this.hideAlert()}
                    confirmText={t('ok')}
                />

                <div className={`${styles.selectionSummary} ${hasSelection ? styles.selectionSummaryActive : ''}`}>
                    {!hasSelection && (
                        <span className={styles.selectionSummaryEmpty}>
                            {t('no_selection_yet')}
                        </span>
                    )}
                    {selectedSpecial !== undefined && !groupSelectorVisible && (() => {
                        const opt = specialOptions.find((o) => o.id === selectedSpecial);
                        return opt ? (
                            <span className={styles.selectionSummaryLabel} style={{ color: opt.color }}>
                                {t(opt.labelKey)}
                            </span>
                        ) : null;
                    })()}
                    {groupSelectorVisible && (
                        <span className={styles.selectionSummaryLabel} style={{ color: '#2e7d32' }}>
                            {selectedGroupCount > 0
                                ? `${selectedGroupCount} ${selectedGroupCount % 10 === 1 && selectedGroupCount % 100 !== 11 ? t('group_selected_singular') : t('groups_selected')}`
                                : t('special_companies')}
                        </span>
                    )}
                </div>

                {showSpecialOptions && (
                    <div>
                        <h4 className={styles.cardTitle}>{t('offer.distribution')}</h4>
                        <div className={styles.card}>
                        <div className={styles.specialGrid}>
                            {specialOptions.map((opt) => {
                                const isSelected =
                                    opt.id === 'groups'
                                        ? groupSelectorVisible
                                        : torgid_special_company_ids.includes(opt.id);
                                return (
                                    <div
                                        key={opt.id}
                                        data-testid={`offer-distribution-${opt.id}`}
                                        className={`${styles.specialCard} ${
                                            isSelected ? styles.specialCardSelected : ''
                                        }`}
                                        style={
                                            isSelected
                                                ? {
                                                      borderColor: opt.color,
                                                      backgroundColor: opt.color + '15',
                                                  }
                                                : {}
                                        }
                                        onClick={() => this.handleToggle(opt.id)}
                                    >
                                        {isSelected && (
                                            <div
                                                className={styles.checkBadge}
                                                style={{ backgroundColor: opt.color }}
                                            >
                                                <Check style={{ fontSize: 14 }} />
                                            </div>
                                        )}
                                        <span
                                            className={styles.specialLabel}
                                            style={isSelected ? { color: opt.color } : {}}
                                        >
                                            {t(opt.labelKey)}
                                        </span>
                                        {opt.description && (
                                            <span className={styles.specialDescription}>
                                                {opt.description}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        </div>
                    </div>
                )}

                {(groupSelectorVisible || !showSpecialOptions) && (
                    <div>
                        <h4 className={styles.cardTitle}>{t('special_companies')}</h4>
                        <div className={styles.card}>

                        {groups.length > 0 && (
                            <div className={styles.searchWrapper}>
                                <Search className={styles.searchIcon} />
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder={t('search')}
                                    value={searchQuery}
                                    onChange={(e) => this.setState({ searchQuery: e.target.value })}
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        className={styles.searchClear}
                                        onClick={() => this.setState({ searchQuery: '' })}
                                    >
                                        <Close style={{ fontSize: 16 }} />
                                    </button>
                                )}
                            </div>
                        )}

                        {filteredGroups.length === 0 ? (
                            <p style={{ color: '#888', fontSize: '0.9rem', textAlign: 'center', padding: '20px 0' }}>
                                {t('no_groups_available')}
                            </p>
                        ) : (() => {
                            const myGroups = filteredGroups.filter((g) => g._isMyGroup);
                            const otherGroups = filteredGroups.filter((g) => !g._isMyGroup);
                            const otherGroupsByCategory = this.groupByCategory(otherGroups);
                            return (
                                <>
                                    {myGroups.length > 0 && (
                                        <div className={styles.categorySection}>
                                            {this.renderGroupGrid(myGroups, t('my_groups'), true)}
                                        </div>
                                    )}
                                    {Object.entries(otherGroupsByCategory).map(
                                        ([category, categoryGroups]) => (
                                            <div key={category} className={styles.categorySection}>
                                                {this.renderGroupGrid(categoryGroups, category, false)}
                                            </div>
                                        )
                                    )}
                                </>
                            );
                        })()}
                    </div>
                    </div>
                )}
            </div>
        );
    }
}

export default withTranslation('', { withRef: true })(Step2);
